/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode } from "react";

import { styled } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { useAccountContext } from "../../strict/app/AccountResolver";
import { Integration } from "../../graphql/types";
import { GqlError } from "../common/GqlError";
import { CoreIcon } from "../common/StatusIcon";
import { DataLoader, EmptyContainer, ErrorContainer, SuccessContainer } from "../DataLoader";
import { PageContainer } from "../ui/common";
import { EmptyContainer as Empty } from "../ui/EmptyContainer";
import { useListIntegrations } from "./IntegrationHooks";

interface IntegrationItemProps {
  selected: boolean;
}

const IntegrationItem = styled("div")<IntegrationItemProps>(({ theme, selected }) => ({
  minHeight: "20px",
  lineHeight: "20px",
  wordBreak: "break-word",
  padding: "10px",
  backgroundColor: selected ? theme.palette.surfaceLight20.main : "",
  "&:hover": {
    backgroundColor: theme.palette.surfaceLight30.main,
    cursor: "pointer",
    userSelect: "none",
  },
}));

const Container = styled("div")`
  padding-bottom: 20px;
`;

const IntegrationTitle = styled("div")`
  font-size: 15px;
  font-weight: 700;
  text-transform: uppercase;
`;

const PullRight = styled("div")`
  float: right;
`;

const IntegrationTable = ({ integrations, selected }: { integrations: Integration[]; selected: string }) => {
  const { urlName } = useAccountContext();
  const navigate = useNavigate();

  const byTitle: { [key: string]: Integration[] } = {};
  integrations.forEach((integration: any) => {
    const title = integration.meta.short_title;
    if (byTitle[title]) {
      byTitle[title].push(integration);
    } else {
      byTitle[title] = [integration];
    }
  });

  const strcmp = (s1: string, s2: string) => {
    // prettier-ignore
    return s1 < s2 ? -1 :
           s1 > s2 ? 1 :
            0;
  };

  const titles = Object.keys(byTitle);
  titles.sort((s1, s2) => {
    return strcmp(s1.toLowerCase(), s2.toLowerCase());
  });

  const sections = titles.map((title) => {
    const integrations = byTitle[title];

    integrations.sort((s1: any, s2: any) => {
      return strcmp(s1.sortName().toLowerCase(), s2.sortName().toLowerCase());
    });

    const xs = integrations.map((integration: Integration | any) => {
      let statusIcon: ReactNode | null = null;
      if (integration.status === "degraded") {
        statusIcon = <CoreIcon type="warning" />;
      } else if (integration.status !== "active") {
        statusIcon = <CoreIcon type="error" />;
      }
      return (
        <IntegrationItem
          key={integration.id}
          onClick={() => integration.navigateTo(urlName, integration.id, navigate, "edit")}
          selected={integration.id === selected}
          className="listItem"
        >
          {integration.displayName()}
          <PullRight>{statusIcon}</PullRight>
        </IntegrationItem>
      );
    });

    return (
      <Container key={title}>
        <IntegrationTitle className="listSectionTitle">{title}</IntegrationTitle>
        {xs}
      </Container>
    );
  });

  return <Container id="integrationList">{sections}</Container>;
};

const IntegrationList = ({ selected }: { selected: string }) => {
  const { loading, error, results } = useListIntegrations();

  return (
    <PageContainer>
      <DataLoader loading={loading} error={error} success={results.length > 0}>
        <ErrorContainer>
          <GqlError error={error} />
        </ErrorContainer>
        <EmptyContainer>
          <Empty title="No integrations" />
        </EmptyContainer>
        <SuccessContainer>
          <IntegrationTable integrations={results} selected={selected} />
        </SuccessContainer>
      </DataLoader>
    </PageContainer>
  );
};

export { IntegrationList };
